import { useState, useEffect, useRef } from 'react';
interface TruncatedTextProps {
  text: string;
  maxLines?: number;
}
export function TruncatedText({ text, maxLines = 3 }: TruncatedTextProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number | null;
    left: number;
    bottom: number | null;
    placement: string;
    width: number;
    height: number;
  }>({
    top: null,
    left: 0,
    bottom: null,
    placement: 'top',
    width: 300,
    height: 80,
  });
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const calculateTooltipDimensions = (text: string) => {
    // 根据文本长度计算合适的宽度和高度
    const textLength = text.length;
    let width = 200; // 最小宽度
    let estimatedHeight = 40; // 最小高度
    // 根据文本长度动态调整宽度
    if (textLength < 50) {
      width = Math.max(200, textLength * 6); // 短文本：每字符约6px
    } else if (textLength < 150) {
      width = 300; // 中等文本：固定300px
    } else if (textLength < 300) {
      width = 400; // 较长文本：400px
    } else {
      width = 500; // 很长文本：500px
    }
    // 根据文本长度估算高度（考虑换行）
    const charsPerLine = Math.floor(width / 7); // 每行大约能显示的字符数
    const estimatedLines = Math.ceil(textLength / charsPerLine);
    estimatedHeight = Math.max(40, estimatedLines * 18 + 20); // 每行18px + 内边距
    return { width, height: estimatedHeight };
  };
  const calculateTooltipPosition = (element: HTMLElement, text: string) => {
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const { width: tooltipWidth, height: tooltipHeight } =
      calculateTooltipDimensions(text);
    const offset = 8;
    // 默认居中对齐到元素
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    let placement = 'top';
    let bottom = null;
    let top = null;
    // 检查右边界
    if (left + tooltipWidth > viewportWidth) {
      left = viewportWidth - tooltipWidth - 16;
    }
    // 检查左边界
    if (left < 16) {
      left = 16;
    }
    // 检查上方是否有足够空间
    if (rect.top - tooltipHeight - offset < 16) {
      // 上方空间不够，显示在下方
      placement = 'bottom';
      top = rect.bottom + offset;
      // 检查下方是否也会超出视口
      if (top + tooltipHeight > viewportHeight - 16) {
        // 下方也不够，调整到视口内
        top = viewportHeight - tooltipHeight - 16;
      }
    } else {
      // 上方有足够空间，使用 bottom 定位
      bottom = viewportHeight - rect.top + offset;
    }
    return {
      top,
      left,
      bottom,
      placement,
      width: tooltipWidth,
      height: tooltipHeight,
    };
  };
  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    const position = calculateTooltipPosition(e.currentTarget, text);
    setTooltipPosition(position);
    setShowTooltip(true);
  };
  const handleMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
  };
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="text-sm text-gray-700 line-clamp-3"
        style={{
          WebkitLineClamp: maxLines,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {text || 'N/A'}
      </div>
      {showTooltip && text && text.trim() !== 'N/A' && (
        <div
          className="fixed z-50 p-3 bg-gray-900 text-white text-xs rounded shadow-lg break-words"
          style={{
            ...(tooltipPosition.top !== null && {
              top: `${tooltipPosition.top}px`,
            }),
            ...(tooltipPosition.bottom !== null && {
              bottom: `${tooltipPosition.bottom}px`,
            }),
            left: `${tooltipPosition.left}px`,
            width: `${tooltipPosition.width}px`,
            maxHeight: '200px',
            overflowY: tooltipPosition.height > 200 ? 'auto' : 'visible',
          }}
          onMouseEnter={() => {
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current);
            }
          }}
          onMouseLeave={handleMouseLeave}
        >
          <div className="break-words whitespace-normal">{text}</div>
          {/* 小箭头 */}
          <div
            className={`absolute w-0 h-0 border-l-4 border-r-4 border-l-transparent border-r-transparent ${
              tooltipPosition.placement === 'top'
                ? 'top-full border-t-4 border-t-gray-900'
                : 'bottom-full border-b-4 border-b-gray-900'
            }`}
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      )}
    </div>
  );
}
